import type { AnalysisMetadata, IMeasurement, Segmentation } from '@core';

import { VISUALIZER_CATEGORIES, VISUALIZER_CATEGORY_KEYS } from './constants';

import type { FrameFirstSkeleton, FrameFirstClubTrack } from './skeletonUtils';
import { VisualizationAxis, VisualizationMeasurement } from './mappings';

export type SkeletalAnnotation = {
    category: string;
    name: string;
};

export interface Data3D {
    skeleton: FrameFirstSkeleton;
    bones: { start_point_id: number; end_point_id: number }[];
    clubTrack: FrameFirstClubTrack;
    segmentation: Segmentation;
    keypointIdToName: KeypointIdToName;
    measurements?: IMeasurement[];
    swingPlane?: [number, number, number, number, number, number];
    golfBallTranslation?: [number, number, number];
    metadata?: AnalysisMetadata;
}

export type KeypointIdToName = Record<string, string>;
export type BoneMapping = Array<{
    end_point_id: number;
    start_point_id: number;
}>;

export type ParamCategory = keyof typeof VISUALIZER_CATEGORIES;
export type DisplacementMode = 'displacement' | 'tracing';
export type SelectParams = {
    // Mutually exclusive: only one of Angles, Joint Angles, or Displacement can be selected
    [VISUALIZER_CATEGORY_KEYS.ANGLES]: string | undefined;
    [VISUALIZER_CATEGORY_KEYS.JOINT_ANGLES]: string | undefined;
    [VISUALIZER_CATEGORY_KEYS.DISPLACEMENT]: string | undefined;
    // Multi-select: can have multiple swing planes and paths selected
    [VISUALIZER_CATEGORY_KEYS.SWING_PLANE]: string[];
    [VISUALIZER_CATEGORY_KEYS.PATHS]: string[];
    displacementMode: DisplacementMode;
};
export type FocusedMeasurement = {
    keypointName: string;
    measurement: string;
    axis: 0 | 1 | 2;
    axisData: VisualizationAxis;
    type: 'displacement' | 'angle' | 'jointAngle' | 'distance';
    measurementType: 'extended' | 'connected';
    measurementData: VisualizationMeasurement;
} | null;

export type ElvaVisualizerProps = {
    fallbackFocus: string | null;
    fallbackFocusFrame?: number; // Frame to get focus keypoint coords from (defaults to 0)
    freezePosition: string | null;
    onCameraInteraction?: () => void; // Called when user manually moves camera
    horizontalOffset?: number; // e.g. 0.15 shifts subject 15% left
    verticalOffset?: number; // e.g. 0.15 shifts subject 15% down
    comparisonFrame?: number;
    overlayComparison?: boolean;
};

export const LOCK_TYPE = {
    // Fixed global camera angles (used for displacement visualizations)
    STATIC: 'static',
    // Dynamically computed from Euler angles (used for angle visualizations)
    INTRINSIC: 'intrinsic',
    // Camera perpendicular to plane defined by three keypoints (used for joint angles)
    PLANE: 'plane',
} as const;

export type LockType = (typeof LOCK_TYPE)[keyof typeof LOCK_TYPE];

export type ParamAxisLock =
    | {
          type: typeof LOCK_TYPE.STATIC;
          azimuthal: number;
          polar: number;
      }
    | {
          type: typeof LOCK_TYPE.INTRINSIC;
          axisIndex: 0 | 1 | 2;
          measurementName: string;
      }
    | {
          type: typeof LOCK_TYPE.PLANE;
      };
